import { useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import type { JSX, ReactNode } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet.js';

/**
 * Panneau de détail porté par l'URL (`?id=`) — `06` §8 et `README-shell.md`
 * § « Sheet porté par l'URL ». La liste reste montée dessous ; ouvrir pousse
 * une entrée d'historique (le retour navigateur referme). Fermer (Échap,
 * bouton, voile) revient en arrière quand cette entrée a été poussée par un
 * clic de ligne dans la session, pour que précédent/suivant restent
 * cohérents ; ouvert par une URL chargée directement, il retire `id` en
 * `replace`. Les autres paramètres (onglet, filtres, page, tri) sont
 * conservés dans les deux sens.
 */
export interface DetailSheetParam {
  id: string | null;
  openDetail: (id: string) => void;
  closeDetail: () => void;
}

/**
 * Clés d'historique (`__TSR_key`) des entrées poussées par `openDetail`
 * pendant la session. Au niveau du module : survit au démontage d'un onglet,
 * pas au rechargement de la page (on retombe alors sur `replace`).
 */
const pushedDetailEntries = new Set<string>();

/**
 * `param` : nom du paramètre d'URL (défaut `id`), pour une page dont `id` est
 * déjà pris ou qui porte plusieurs panneaux.
 */
export function useDetailSheetParam(param = 'id'): DetailSheetParam {
  const rawSearch: unknown = useSearch({ strict: false });
  const navigate = useNavigate();
  const router = useRouter();
  const rawId =
    typeof rawSearch === 'object' && rawSearch !== null
      ? (rawSearch as Record<string, unknown>)[param]
      : undefined;
  const id = typeof rawId === 'string' && rawId !== '' ? rawId : null;

  function currentEntryKey(): string | undefined {
    const state = router.state.location.state;
    return state.__TSR_key ?? state.key;
  }

  function openDetail(nextId: string): void {
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, [param]: nextId }),
    }).then(() => {
      const key = currentEntryKey();
      if (key !== undefined) {
        pushedDetailEntries.add(key);
      }
    });
  }

  function closeDetail(): void {
    const key = currentEntryKey();
    // La clé reste connue : revenue par « suivant », l'entrée suit toujours la liste.
    if (key !== undefined && pushedDetailEntries.has(key)) {
      router.history.back();
      return;
    }
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, [param]: undefined }),
      replace: true,
    });
  }

  return { id, openDetail, closeDetail };
}

export interface DetailSheetProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
}

/**
 * Tiroir de consultation (lecture seule) à droite, 560 px, plein écran sous
 * `sm`. Sans `SheetFooter` : aucune action primaire dans un panneau de
 * journal.
 */
export function DetailSheet({
  open,
  onClose,
  title,
  description,
  children,
}: DetailSheetProps): JSX.Element {
  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <SheetContent
        className="overscroll-contain sm:max-w-[35rem]"
        {...(description === undefined ? { 'aria-describedby': undefined } : {})}
      >
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          {description === undefined ? null : <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>
        {children}
      </SheetContent>
    </Sheet>
  );
}
